<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { importLibrary } from '@/utils/google-maps-loader';
import { useCompanyMapCenter, COUNTRY_ZOOM } from '@/composables/useCompanyMapCenter';
import {
  isEnrichedShift,
  type RouteAssignment, type RoutePlanCaregiver, type ExistingShiftStep, type EnrichedExistingShift,
} from '@/api/route-plans';
import type { AssignmentsByCaregiver } from '@/composables/useAssignmentsByCaregiver';
import {
  locationKey, groupAssignmentsByLocation,
  type AssignmentLocationGroup,
} from '@/utils/route-plan-map';
import {
  assignmentStepKey, existingShiftStepKey, displaySeqByKey,
} from '@/utils/route-plan-merged-steps';

const { t } = useI18n();
const companyMapCenter = useCompanyMapCenter();

interface Props {
  assignmentsByCaregiver: AssignmentsByCaregiver;
  routePlanCaregivers: RoutePlanCaregiver[];
  colorMap: Record<number, string>;
  highlightedCaregiverId?: number | null;
  caregiverExistingShifts?: Record<number, ExistingShiftStep[]>;
}

const props = withDefaults(defineProps<Props>(), {
  highlightedCaregiverId: null,
  caregiverExistingShifts: undefined,
});

const INITIALS_MAX_LENGTH = 2;
const DIMMED_OPACITY = 0.15;
const STROKE_OPACITY = 0.7;
const DASH_OPACITY = 0.6;
const STROKE_WEIGHT = 3;
const BOUNDS_PADDING = 30;

let mapInstance: google.maps.Map | null = null;
let allMarkers: google.maps.marker.AdvancedMarkerElement[] = [];
let allPolylines: google.maps.Polyline[] = [];
let infoWindow: google.maps.InfoWindow | null = null;

const FALLBACK_COLOR = '#1976D2';

function getColor(caregiverId: number): string {
  return props.colorMap[caregiverId] ?? FALLBACK_COLOR;
}

function getInitials(name: string) {
  return name.split(' ').map(w => w[0]).join('')
    .substring(0, INITIALS_MAX_LENGTH)
    .toUpperCase();
}

function getCaregiverInfo(caregiverId: number) {
  return props.routePlanCaregivers.find(c => c.caregiverId === caregiverId);
}

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;

  return div.innerHTML;
}

function createSpan(className: string, text: string): HTMLSpanElement {
  const el = document.createElement('span');
  el.className = className;
  el.textContent = text;

  return el;
}

function wrapInAnchor(inner: HTMLElement): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'map-marker-anchor';
  wrapper.appendChild(inner);

  return wrapper;
}

interface MarkerPinOptions {
  seq: number; name: string; color: string; badge?: string; cssModifier?: string;
}

function createMarkerPin({ seq, name, color, badge, cssModifier }: MarkerPinOptions): HTMLElement {
  const container = document.createElement('div');
  container.className = `map-marker-pin${cssModifier ? ` ${cssModifier}` : ''}`;
  container.style.backgroundColor = color;
  container.append(
    createSpan('map-marker-seq', String(seq)),
    createSpan('map-marker-initials', getInitials(name)),
  );
  if (badge) container.appendChild(createSpan('map-marker-badge', badge));

  return wrapInAnchor(container);
}

function createHomeMarkerContent(color: string): HTMLElement {
  const el = document.createElement('div');
  el.className = 'map-home-marker';
  el.style.backgroundColor = color;
  el.style.borderColor = color;

  return wrapInAnchor(el);
}

function existingShiftPopupLines(existingShifts: ExistingShiftStep[], assignment: RouteAssignment): string {
  return existingShifts
    .filter(isEnrichedShift)
    .filter(s => locationKey(s) === locationKey(assignment))
    .map(s => {
      let line = `<em>${escapeHtml(t('routePlan.existingShift'))}</em> `;
      line += `${escapeHtml(s.humanStartTime)} - ${escapeHtml(s.humanEndTime)}`;
      if (s.humanTravelTime) line += ` · ${escapeHtml(s.humanTravelTime)}`;

      return line;
    })
    .join('<br>');
}

function visitPopupLines(visits: RouteAssignment[]): string {
  return visits
    .map(v => {
      let line = `<strong>#${v.sequence}</strong> ${escapeHtml(v.humanArrivalTime)}`;
      line += ` - ${escapeHtml(v.humanDepartureTime)}`;
      if (v.humanTravelTime) line += ` · ${escapeHtml(v.humanTravelTime)}`;

      return line;
    })
    .join('<br>');
}

function buildPopupContent(
  group: AssignmentLocationGroup, existingShifts: ExistingShiftStep[],
): string {
  const { first } = group;
  let html = `<strong>${escapeHtml(first.patientFullName)}</strong>`;
  if (first.patientAddressLabel) html += `<br><em>${escapeHtml(first.patientAddressLabel)}</em>`;
  html += `<br>${escapeHtml(first.patientAddress ?? '')}<br><br>`;
  html += visitPopupLines(group.visits);

  const shiftLines = existingShiftPopupLines(existingShifts, first);
  if (shiftLines) html += `<br>${shiftLines}`;

  return html;
}

// Shifts outside the viewer's scope come without a patient name.
function existingShiftName(shift: EnrichedExistingShift): string {
  return shift.patientFullName ?? t('routePlan.existingShift');
}

function buildExistingShiftPopupContent(shift: ExistingShiftStep): string {
  if (!isEnrichedShift(shift)) return `<em>${escapeHtml(t('routePlan.existingShift'))}</em>`;

  let html = `<strong>${escapeHtml(existingShiftName(shift))}</strong>`;
  html += `<br>${escapeHtml(shift.patientAddress ?? '')}<br><br>`;
  html += `${escapeHtml(shift.humanStartTime)} - ${escapeHtml(shift.humanEndTime)}`;
  if (shift.humanTravelTime) html += ` · ${escapeHtml(shift.humanTravelTime)}`;
  html += `<br><em>${escapeHtml(t('routePlan.existingShift'))}</em>`;
  if (shift.dropped) html += `<br><strong>${escapeHtml(t('routePlan.notOptimized'))}</strong>`;

  return html;
}

function clearMap() {
  allMarkers.forEach(m => {
    m.map = null;
  });
  allPolylines.forEach(p => p.setMap(null));
  allMarkers = [];
  allPolylines = [];
}

interface RoutePoint {
  lat: number;
  lng: number;
  isExistingShift: boolean;
}

interface RouteBuilderCtx {
  AdvancedMarker: typeof google.maps.marker.AdvancedMarkerElement;
  bounds: google.maps.LatLngBounds;
  color: string;
  caregiverId: number;
  seqMap: DisplaySeqMap;
}

function addHomeMarker(ctx: RouteBuilderCtx, caregiver: RoutePlanCaregiver) {
  if (!caregiver.caregiverLatitude || !caregiver.caregiverLongitude) return;

  const homePos = { lat: caregiver.caregiverLatitude, lng: caregiver.caregiverLongitude };
  ctx.bounds.extend(homePos);
  const content = createHomeMarkerContent(ctx.color);
  content.dataset.caregiverId = String(ctx.caregiverId);
  const homeMarker = new ctx.AdvancedMarker({
    map: mapInstance,
    position: homePos,
    content,
    title: caregiver.caregiverFullName,
    gmpClickable: true,
  });

  const popupHtml = `<strong>${escapeHtml(caregiver.caregiverFullName)}</strong>` +
    `<br>${escapeHtml(t('routePlan.home'))}`;
  homeMarker.addEventListener('gmp-click', () => {
    infoWindow!.setContent(popupHtml);
    infoWindow!.open(mapInstance, homeMarker);
  });
  allMarkers.push(homeMarker);
}

// Display sequence shared with the list and timeline views via route-plan-merged-steps.
type DisplaySeqMap = Record<string, number>;

function addVisitMarker(
  ctx: RouteBuilderCtx, group: AssignmentLocationGroup, existingShifts: ExistingShiftStep[],
) {
  const { first } = group;
  const seq = ctx.seqMap[assignmentStepKey(first)] ?? first.sequence;
  const badge = group.visits.length > 1 ? `×${group.visits.length}` : undefined;
  const content = createMarkerPin({ seq, name: first.patientFullName, color: ctx.color, badge });
  content.dataset.caregiverId = String(ctx.caregiverId);
  const marker = new ctx.AdvancedMarker({
    map: mapInstance,
    position: { lat: first.patientLatitude!, lng: first.patientLongitude! },
    content,
    gmpClickable: true,
  });

  const popupHtml = buildPopupContent(group, existingShifts);
  marker.addEventListener('gmp-click', () => {
    infoWindow!.setContent(popupHtml);
    infoWindow!.open(mapInstance, marker);
  });
  allMarkers.push(marker);
}

function addExistingShiftMarker(ctx: RouteBuilderCtx, shift: ExistingShiftStep) {
  if (!isEnrichedShift(shift) || !shift.patientLatitude || !shift.patientLongitude) return;

  const pos = { lat: shift.patientLatitude, lng: shift.patientLongitude };
  ctx.bounds.extend(pos);

  const seq = ctx.seqMap[existingShiftStepKey(shift)] ?? 0;
  const content = createMarkerPin({
    seq, name: existingShiftName(shift), color: ctx.color,
    cssModifier: shift.dropped ?
      'map-marker-pin--existing map-marker-pin--dropped' : 'map-marker-pin--existing',
  });
  content.dataset.caregiverId = String(ctx.caregiverId);
  const marker = new ctx.AdvancedMarker({
    map: mapInstance,
    position: pos,
    content,
    gmpClickable: true,
  });

  const popupHtml = buildExistingShiftPopupContent(shift);
  marker.addEventListener('gmp-click', () => {
    infoWindow!.setContent(popupHtml);
    infoWindow!.open(mapInstance, marker);
  });
  allMarkers.push(marker);
}

// Returns the set of location keys that got a visit marker, so existing-shift markers at those
// same locations can be skipped (their visits already appear in the marker's popup).
function addVisitMarkers(
  ctx: RouteBuilderCtx, assignments: RouteAssignment[], existingShifts: ExistingShiftStep[],
): Set<string> {
  const groups = groupAssignmentsByLocation(assignments);

  for (const group of groups) {
    ctx.bounds.extend({ lat: group.first.patientLatitude!, lng: group.first.patientLongitude! });
    addVisitMarker(ctx, group, existingShifts);
  }

  return new Set(groups.map(g => g.key));
}

type TimePoint = { time: string; lat: number; lng: number; isExistingShift: boolean };

function toTimePoints(assignments: RouteAssignment[], existingShifts: ExistingShiftStep[]): TimePoint[] {
  const fromAssignments = assignments
    .filter(a => a.patientLatitude && a.patientLongitude)
    .map(a => ({ time: a.arrivalTime, lat: a.patientLatitude!, lng: a.patientLongitude!, isExistingShift: false }));

  const fromShifts = existingShifts
    .filter(isEnrichedShift)
    .filter(s => s.patientLatitude && s.patientLongitude)
    .map(s => ({ time: s.startTime, lat: s.patientLatitude!, lng: s.patientLongitude!, isExistingShift: true }));

  return [...fromAssignments, ...fromShifts]
    .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
}

function buildMergedRoutePoints(
  assignments: RouteAssignment[], existingShifts: ExistingShiftStep[],
): RoutePoint[] {
  const sorted = toTimePoints(assignments, existingShifts);

  // Collapse consecutive points at the same location (visit + existing shift for same patient).
  // Keep isExistingShift=false if any point at that location is a visit.
  // Equality check is safe: coordinates originate from the same DB record (patient.latitude/longitude).
  return sorted.reduce<RoutePoint[]>((acc, p) => {
    const prev = acc[acc.length - 1];
    if (prev && prev.lat === p.lat && prev.lng === p.lng) {
      if (!p.isExistingShift) prev.isExistingShift = false;

      return acc;
    }
    acc.push({ lat: p.lat, lng: p.lng, isExistingShift: p.isExistingShift });

    return acc;
  }, []);
}

function makeArrowIcon(arrowColor: string): google.maps.IconSequence {
  return {
    icon: {
      path: google.maps.SymbolPath.FORWARD_OPEN_ARROW,
      scale: 2, strokeColor: arrowColor, strokeWeight: STROKE_WEIGHT,
    },
    offset: '50%',
  };
}

// strokeOpacity .6 is the default dashed-line appearance (distinct from DIMMED_OPACITY used for highlight dimming)
function makeDashPattern(dashColor: string): google.maps.IconSequence {
  return {
    icon: { path: 'M 0,-1 0,1', scale: 3, strokeOpacity: DASH_OPACITY, strokeColor: dashColor },
    offset: '0', repeat: '12px',
  };
}

function segmentPolylineOptions(from: RoutePoint, to: RoutePoint, color: string) {
  const dashed = from.isExistingShift || to.isExistingShift;

  return {
    path: [{ lat: from.lat, lng: from.lng }, { lat: to.lat, lng: to.lng }],
    strokeColor: color,
    strokeOpacity: dashed ? 0 : STROKE_OPACITY,
    strokeWeight: STROKE_WEIGHT,
    icons: dashed ? [makeDashPattern(color), makeArrowIcon(color)] : [makeArrowIcon(color)],
    map: mapInstance,
  };
}

function addRoutePolylines(routePoints: RoutePoint[], color: string, caregiverId: number) {
  for (let i = 0; i < routePoints.length - 1; i++) {
    const from = routePoints[i]!;
    const to = routePoints[i + 1]!;
    const polyline = new google.maps.Polyline(segmentPolylineOptions(from, to, color));
    polyline.set('caregiverId', caregiverId);
    polyline.set('isDashed', from.isExistingShift || to.isExistingShift);
    allPolylines.push(polyline);
  }
}

function getCaregiverHomePos(caregiver: RoutePlanCaregiver | undefined): RoutePoint | null {
  if (!caregiver?.caregiverLatitude || !caregiver?.caregiverLongitude) return null;

  return { lat: caregiver.caregiverLatitude, lng: caregiver.caregiverLongitude, isExistingShift: false };
}

function buildFullRoute(
  homePos: RoutePoint | null, assignments: RouteAssignment[], existingShifts: ExistingShiftStep[],
): RoutePoint[] {
  const mergedPoints = buildMergedRoutePoints(assignments, existingShifts);
  const fullRoute: RoutePoint[] = [];
  if (homePos) fullRoute.push(homePos);
  fullRoute.push(...mergedPoints);
  if (homePos) fullRoute.push(homePos);

  return fullRoute;
}

function buildCaregiverRoute(
  ctx: RouteBuilderCtx, cgId: number, assignments: RouteAssignment[],
) {
  const caregiver = getCaregiverInfo(cgId);
  const existingShifts = props.caregiverExistingShifts?.[cgId] ?? [];
  ctx.seqMap = displaySeqByKey(assignments, existingShifts);

  if (caregiver) addHomeMarker(ctx, caregiver);
  const visitLocationKeys = addVisitMarkers(ctx, assignments, existingShifts);

  // Only add grey markers for existing shifts at a location that has no visit marker; a shift at a
  // distinct address still gets its own pin.
  existingShifts
    .filter(s => !isEnrichedShift(s) || !visitLocationKeys.has(locationKey(s)))
    .forEach(shift => addExistingShiftMarker(ctx, shift));

  const fullRoute = buildFullRoute(getCaregiverHomePos(caregiver), assignments, existingShifts);
  addRoutePolylines(fullRoute, ctx.color, ctx.caregiverId);

  return fullRoute.length > 1;
}

function buildAllRoutes(
  AdvancedMarker: typeof google.maps.marker.AdvancedMarkerElement,
): boolean {
  const bounds = new google.maps.LatLngBounds();
  let hasBounds = false;

  // assignmentsByCaregiver already includes caregivers with only existing shifts (no
  // new visits), so committed shifts the optimizer couldn't place are never hidden.
  Object.entries(props.assignmentsByCaregiver).forEach(([caregiverIdStr, group]) => {
    const cgId = parseInt(caregiverIdStr, 10);
    const ctx: RouteBuilderCtx = {
      AdvancedMarker, bounds, seqMap: {},
      color: getColor(cgId), caregiverId: cgId,
    };
    if (buildCaregiverRoute(ctx, cgId, group.assignments)) hasBounds = true;
  });

  if (hasBounds) {
    mapInstance!.fitBounds(bounds, {
      top: BOUNDS_PADDING, right: BOUNDS_PADDING,
      bottom: BOUNDS_PADDING, left: BOUNDS_PADDING,
    });
  }

  return hasBounds;
}

const HIGHLIGHTED_MARKER_Z_INDEX = 1;
const DIMMED_MARKER_Z_INDEX = 0;

// While a route is highlighted, both sides need an explicit zIndex: mixing an explicit value
// with Google's default (latitude-based) stacking wouldn't guarantee the highlighted marker wins.
function highlightZIndex(active: boolean, highlightedId: number | null): number | null {
  if (!highlightedId) return null;

  return active ? HIGHLIGHTED_MARKER_Z_INDEX : DIMMED_MARKER_Z_INDEX;
}

function applyMarkerHighlight(
  marker: google.maps.marker.AdvancedMarkerElement, highlightedId: number | null,
) {
  const el = marker.content as HTMLElement;
  if (!el) return;

  const cgId = Number(el.dataset.caregiverId);
  const active = !highlightedId || cgId === highlightedId;
  el.style.opacity = active ? '1' : String(DIMMED_OPACITY);
  // Markers of different caregivers stack at the same coordinates when they visit the same
  // patient. Bring the highlighted route's markers to the front and let clicks pass through
  // the dimmed ones, so the popup shows the selected route's visits, not the topmost marker's.
  el.style.pointerEvents = active ? '' : 'none';
  marker.zIndex = highlightZIndex(active, highlightedId);
}

function applyHighlight(highlightedId: number | null) {
  allMarkers.forEach(m => applyMarkerHighlight(m, highlightedId));

  allPolylines.forEach(p => {
    const cgId = Number(p.get('caregiverId'));
    const visible = !highlightedId || cgId === highlightedId;

    if (p.get('isDashed')) {
      // Dashed lines use icons for rendering (strokeOpacity=0), so adjust icon opacity to dim
      const icons = (p.get('icons') as google.maps.IconSequence[]) || [];
      const dimmedIcons = icons.map(ic => {
        const icon = ic.icon as google.maps.Symbol;

        return { ...ic, icon: { ...icon, strokeOpacity: visible ? DASH_OPACITY : DIMMED_OPACITY } };
      });
      p.setOptions({ icons: dimmedIcons });
    } else {
      p.setOptions({ strokeOpacity: visible ? STROKE_OPACITY : DIMMED_OPACITY });
    }
  });
}

async function renderAllRoutes() {
  if (!mapInstance) return;
  clearMap();

  const { AdvancedMarkerElement } = await importLibrary('marker');
  infoWindow = infoWindow || new google.maps.InfoWindow();
  buildAllRoutes(AdvancedMarkerElement);
  applyHighlight(props.highlightedCaregiverId);
}

watch(() => props.highlightedCaregiverId, applyHighlight);

watch(() => props.assignmentsByCaregiver, renderAllRoutes, { deep: true });

onMounted(async () => {
  const { Map } = await importLibrary('maps');
  const container = document.querySelector('.route-plan-map-container') as HTMLElement;
  if (!container) return;

  mapInstance = new Map(container, {
    center: companyMapCenter.value,
    zoom: COUNTRY_ZOOM,
    mapId: import.meta.env.VITE_GOOGLE_MAPS_MAP_ID,
    zoomControl: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
  });

  await renderAllRoutes();
});

onUnmounted(() => {
  clearMap();
  mapInstance = null;
});
</script>

<template>
  <div class="route-plan-map-container" />
</template>

<style>
.route-plan-map-container {
  width: 100%;
  height: 100%;
  min-height: 400px;
}

.map-marker-pin {
  position: relative;
  color: #fff;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  line-height: 1;
  font-weight: bold;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, .3);
}

.map-marker-pin--existing {
  border: 2px dashed #fff;
  opacity: .65;
}

/* Existing shift the optimizer couldn't place: warning outline so it stands out. */
.map-marker-pin--dropped {
  border: 2px solid #e65100;
  box-shadow: 0 0 0 2px #fb8c00;
  opacity: 1;
}

.map-marker-seq {
  font-size: 9px;
  opacity: .8;
}

.map-marker-initials {
  font-size: 10px;
}

.map-marker-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #333;
  color: #fff;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  font-size: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.map-home-marker {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 3px solid;
  opacity: .8;
}

.map-marker-anchor {
  position: relative;
  width: 0;
  height: 0;
  overflow: visible;
}

.map-marker-anchor > .map-marker-pin {
  position: absolute;
  left: -16px;
  top: -16px;
}

.map-marker-anchor > .map-home-marker {
  position: absolute;
  left: -11px;
  top: -11px;
}
</style>
